import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Calculator, TrendingUp, Building2, Coins, Layers, Activity,
  Loader2, AlertCircle, CheckCircle2, Database, RefreshCw, Info, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const fmt = (n) => new Intl.NumberFormat('de-CH', { maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtInt = (n) => new Intl.NumberFormat('de-CH').format(Math.round(Number(n) || 0));

export const AdminCombinedValuation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dcf, setDcf] = useState({
    fcfBase: 500000, growth1: 8, growth2: 4, wacc: 10, terminalGrowth: 2,
    netDebt: 0, shares: 10000, marginOfSafety: 25,
    depreciation: 0, workingCapitalChange: 0,
  });
  const [wacc, setWaccForm] = useState({
    riskFree: 1.5, beta: 0.8, marketPremium: 5.5, costOfDebt: 2.0,
    equityValue: 0, debtValue: 0, taxRate: 14.93,
  });
  const [waccResult, setWaccResult] = useState(null);
  const [capRate, setCapRate] = useState({ targetCapRate: 5.0, otherAssets: 0, otherLiabilities: 0 });
  const [weights, setWeights] = useState({ dcf: 30, nav: 50, cap: 20 });
  const [scenariosForm, setScenariosForm] = useState({
    best: { growth1: 12, growth2: 6, wacc: 9, terminalGrowth: 2.5 },
    base: { growth1: 8, growth2: 4, wacc: 10, terminalGrowth: 2 },
    worst: { growth1: 3, growth2: 1, wacc: 12, terminalGrowth: 1 },
  });
  const [note, setNote] = useState('');

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loadingDefaults, setLoadingDefaults] = useState(true);

  const loadDefaults = useCallback(async () => {
    setLoadingDefaults(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/combined/defaults', { credentials: 'include' });
      const d = await r.json();
      setDefaults(d);
    } catch (e) {
      console.error('Defaults load failed', e);
    } finally {
      setLoadingDefaults(false);
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/admin/login'); return; }
    loadDefaults();
  }, [user, navigate, loadDefaults]);

  // Auto-Übernehmen aus DCF / Aktienregister / Properties
  const applyDcfDefaults = () => {
    if (!defaults?.lastDcf) {
      toast.error('Keine gespeicherte DCF-Bewertung gefunden');
      return;
    }
    const d = defaults.lastDcf;
    setDcf((prev) => ({
      ...prev,
      fcfBase: d.fcfBase ?? prev.fcfBase,
      growth1: d.growth1 ?? prev.growth1,
      growth2: d.growth2 ?? prev.growth2,
      wacc: d.wacc ?? prev.wacc,
      terminalGrowth: d.terminalGrowth ?? prev.terminalGrowth,
      netDebt: d.netDebt ?? prev.netDebt,
      shares: d.shares ?? prev.shares,
      marginOfSafety: d.marginOfSafety ?? prev.marginOfSafety,
      depreciation: d.depreciation ?? prev.depreciation,
      workingCapitalChange: d.workingCapitalChange ?? prev.workingCapitalChange,
    }));
    toast.success('DCF-Inputs aus letzter Bewertung übernommen');
  };

  const applyAregShares = () => {
    const s = defaults?.areg?.totalShares;
    if (!s) {
      toast.error('Keine Aktienzahl im Aktienregister-Cache verfügbar');
      return;
    }
    setDcf((prev) => ({ ...prev, shares: s }));
    toast.success(`Anzahl Aktien aus Aktienregister: ${fmtInt(s)}`);
  };

  const applyMortgageAsDebt = () => {
    const m = defaults?.properties?.totalMortgage;
    if (!m) {
      toast.error('Keine Hypothek-Summe aus Properties verfügbar');
      return;
    }
    setDcf((prev) => ({ ...prev, netDebt: m }));
    toast.success(`Nettoverschuldung = Σ Hypothek (CHF ${fmtInt(m)})`);
  };

  const computeWacc = async () => {
    setBusy(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/wacc', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(wacc),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Fehler');
      setWaccResult(d);
      setDcf((prev) => ({ ...prev, wacc: d.wacc }));
      toast.success(`WACC berechnet: ${d.wacc}%`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const runCombined = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/combined', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dcf,
          otherAssets: parseFloat(capRate.otherAssets) || 0,
          otherLiabilities: parseFloat(capRate.otherLiabilities) || 0,
          targetCapRate: parseFloat(capRate.targetCapRate) || 5.0,
          weights,
          scenarios: scenariosForm,
          note,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Fehler');
      setResult(d.combined);
      toast.success('Kombinierte Bewertung erstellt');
      setTimeout(() => {
        document.getElementById('combined-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const totalWeight = (parseFloat(weights.dcf) || 0) + (parseFloat(weights.nav) || 0) + (parseFloat(weights.cap) || 0);
  const props = defaults?.properties;
  const propCount = props?.count || 0;
  const aregShares = defaults?.areg?.totalShares;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#C4A55A]/20 bg-black/80 backdrop-blur-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/admin/dashboard" className="text-gray-400 hover:text-[#C4A55A]"><ArrowLeft className="h-5 w-5" /></Link>
          <Layers className="h-6 w-6 text-[#C4A55A]" />
          <h1 className="text-xl font-semibold">Kombinierte Unternehmensbewertung</h1>
          <span className="text-xs text-amber-300 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded ml-2">Nur intern</span>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl space-y-6">
        {/* Ablauf-Hinweis */}
        <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6">
          <h2 className="text-sm uppercase tracking-wider text-[#C4A55A] mb-3">So funktioniert die kombinierte Bewertung</h2>
          <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside">
            <li><strong className="text-white">Datenquellen prüfen</strong> – unten siehst Du, was die App aus Aktienregister, DCF-Modul und Immobilien automatisch findet. Mit „Übernehmen" füllst Du die Inputs in einem Klick.</li>
            <li><strong className="text-white">(Optional) WACC herleiten</strong> – via CAPM aus Beta, Risikofrei-Zinssatz, Marktrisikoprämie und Kapitalstruktur. Der berechnete WACC wird unten direkt in den DCF übernommen.</li>
            <li><strong className="text-white">DCF-, NAV- und Cap-Rate-Inputs überprüfen</strong> – Werte stammen aus den Auto-Quellen, können aber überschrieben werden.</li>
            <li><strong className="text-white">Gewichtung festlegen</strong> (Σ 100 %) – für eine Immobilien-AG typisch DCF 30 / NAV 50 / Cap 20.</li>
            <li><strong className="text-white">Berechnen</strong> – das Ergebnis zeigt Konsens-Preis, alle drei Methoden mit Formel, Szenarien (Best/Base/Worst) und Sensitivitätsmatrix.</li>
          </ol>
        </div>

        {/* SCHRITT 1 – Datenquellen */}
        <section
          className="bg-[#0f0f0f] border border-[#C4A55A]/40 rounded-2xl p-6"
          data-testid="combined-defaults-section"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="bg-[#C4A55A] text-black font-bold rounded-full w-7 h-7 flex items-center justify-center text-sm">1</span>
              <Database className="h-5 w-5 text-[#C4A55A]" />
              <h2 className="text-lg font-semibold">Datenquellen aus Deinem System</h2>
            </div>
            <button
              onClick={loadDefaults}
              disabled={loadingDefaults}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/20 text-gray-300 hover:bg-white/10 inline-flex items-center gap-1.5"
              data-testid="defaults-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loadingDefaults ? 'animate-spin' : ''}`} />
              Aktualisieren
            </button>
          </div>

          {loadingDefaults && !defaults ? (
            <div className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Lade Quellen…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Aktienregister */}
              <DataCard
                label="Aktienregister"
                accent
                empty={!aregShares}
                emptyHint="Areg-Cache leer oder nicht konfiguriert"
                action={aregShares ? { label: `→ ${fmtInt(aregShares)} Aktien übernehmen`, onClick: applyAregShares, testId: 'apply-areg-shares' } : null}
              >
                {aregShares ? (
                  <>
                    <Row label="Total Aktien" value={fmtInt(aregShares)} />
                    <p className="text-[10px] text-gray-500 mt-2">
                      Wird in DCF/NAV als Basis für „pro Aktie" verwendet.
                    </p>
                  </>
                ) : null}
              </DataCard>

              {/* Letzter DCF */}
              <DataCard
                label="Letzte DCF-Bewertung"
                accent
                empty={!defaults?.lastDcf}
                emptyHint={<>Noch keine gespeicherte DCF gefunden. <Link to="/admin/valuation" className="text-[#C4A55A] underline">jetzt DCF erstellen</Link></>}
                action={defaults?.lastDcf ? { label: '→ Alle DCF-Inputs übernehmen', onClick: applyDcfDefaults, testId: 'apply-dcf-defaults' } : null}
              >
                {defaults?.lastDcf ? (
                  <>
                    <Row label="FCF Basis" value={`CHF ${fmtInt(defaults.lastDcf.fcfBase)}`} />
                    <Row label="WACC" value={`${defaults.lastDcf.wacc}%`} />
                    <Row label="g₁ / g₂" value={`${defaults.lastDcf.growth1}% / ${defaults.lastDcf.growth2}%`} />
                    <Row label="Aktien" value={fmtInt(defaults.lastDcf.shares)} />
                  </>
                ) : null}
              </DataCard>

              {/* Immobilien */}
              <DataCard
                label={`Immobilien-Portfolio (${propCount})`}
                accent
                empty={propCount === 0}
                emptyHint={<>Keine Immobilien erfasst. <Link to="/admin/properties" className="text-[#C4A55A] underline">jetzt anlegen</Link></>}
                action={props?.totalMortgage ? { label: `→ Σ Hypothek (CHF ${fmtInt(props.totalMortgage)}) als Schuld übernehmen`, onClick: applyMortgageAsDebt, testId: 'apply-mortgage' } : null}
              >
                {propCount > 0 ? (
                  <>
                    <Row label="Σ Marktwert" value={`CHF ${fmtInt(props.totalMarketValue)}`} />
                    <Row label="Σ Hypothek" value={`CHF ${fmtInt(props.totalMortgage)}`} sub="(Cap-Rate-Schuld)" />
                    <Row label="Σ NOI" value={`CHF ${fmtInt(props.totalNOI)}`} />
                    <Row label="Portfolio Cap" value={`${props.portfolioCapRate}%`} />
                    <p className="text-[10px] text-emerald-400 mt-2">✓ Fliesst automatisch in NAV + Cap Rate.</p>
                  </>
                ) : null}
              </DataCard>
            </div>
          )}
        </section>

        {/* SCHRITT 2 – WACC */}
        <section className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6" data-testid="combined-wacc-section">
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-white/10 text-white font-bold rounded-full w-7 h-7 flex items-center justify-center text-sm">2</span>
            <Activity className="h-5 w-5 text-[#C4A55A]" />
            <h2 className="text-lg font-semibold">WACC-Helper (CAPM) <span className="text-xs text-gray-500 font-normal">– optional</span></h2>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Eigenkapitalkosten via CAPM + Fremdkapitalkosten gewichtet nach Kapitalstruktur. Ergebnis fliesst automatisch ins WACC-Feld in Schritt 3.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Num label="Risikofreier Zins (%) – 10y CH Bond" value={wacc.riskFree} step="0.1" onChange={(v) => setWaccForm({...wacc, riskFree: v})} testId="wacc-rf" />
            <Num label="Beta" value={wacc.beta} step="0.05" onChange={(v) => setWaccForm({...wacc, beta: v})} testId="wacc-beta" />
            <Num label="Marktrisikoprämie (%)" value={wacc.marketPremium} step="0.1" onChange={(v) => setWaccForm({...wacc, marketPremium: v})} testId="wacc-mp" />
            <Num label="Fremdkapitalzins (%)" value={wacc.costOfDebt} step="0.1" onChange={(v) => setWaccForm({...wacc, costOfDebt: v})} testId="wacc-rd" />
            <Num label="Eigenkapital (CHF)" value={wacc.equityValue} onChange={(v) => setWaccForm({...wacc, equityValue: v})} testId="wacc-equity" />
            <Num label="Fremdkapital (CHF)" value={wacc.debtValue} onChange={(v) => setWaccForm({...wacc, debtValue: v})} testId="wacc-debt" />
            <Num label="Steuersatz (%)" value={wacc.taxRate} step="0.1" onChange={(v) => setWaccForm({...wacc, taxRate: v})} testId="wacc-tax" />
            <button onClick={computeWacc} disabled={busy} data-testid="wacc-calc-btn"
              className="self-end px-3 py-2 rounded-lg bg-[#C4A55A]/15 border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/25 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'WACC berechnen'}
            </button>
          </div>
          {waccResult && (
            <div className="bg-black/40 rounded-lg p-3 text-sm flex flex-wrap gap-4" data-testid="wacc-result">
              <span>Eigenkapitalkosten (CAPM): <strong className="text-white">{waccResult.costOfEquity} %</strong></span>
              <span>→ <strong className="text-[#C4A55A]">WACC = {waccResult.wacc} %</strong></span>
              <span className="text-xs text-gray-500">(in DCF unten übernommen)</span>
            </div>
          )}
        </section>

        {/* SCHRITT 3 – DCF / NAV / Cap-Rate Inputs */}
        <section className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6" data-testid="combined-dcf-section">
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-white/10 text-white font-bold rounded-full w-7 h-7 flex items-center justify-center text-sm">3</span>
            <TrendingUp className="h-5 w-5 text-[#C4A55A]" />
            <h2 className="text-lg font-semibold">DCF-, NAV- und Cap-Rate-Inputs</h2>
          </div>

          <h3 className="text-xs uppercase tracking-wider text-[#C4A55A] mt-2 mb-2">3a. DCF (Ertragswert)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Num label="Free Cash Flow Basis (CHF)" value={dcf.fcfBase} onChange={(v) => setDcf({...dcf, fcfBase: v})} testId="cdcf-fcf" />
            <Num label="Abschreibungen p.a. (CHF)" value={dcf.depreciation} onChange={(v) => setDcf({...dcf, depreciation: v})} hint="wird zum FCF addiert" testId="cdcf-depr" />
            <Num label="Δ Working Capital (CHF)" value={dcf.workingCapitalChange} onChange={(v) => setDcf({...dcf, workingCapitalChange: v})} hint="Positiv = bindet Cash" testId="cdcf-wc" />
            <Num label="Anzahl Aktien" value={dcf.shares} onChange={(v) => setDcf({...dcf, shares: v})} testId="cdcf-shares" />
            <Num label="Wachstum P1 (% J1-5)" value={dcf.growth1} step="0.5" onChange={(v) => setDcf({...dcf, growth1: v})} testId="cdcf-g1" />
            <Num label="Wachstum P2 (% J6-10)" value={dcf.growth2} step="0.5" onChange={(v) => setDcf({...dcf, growth2: v})} testId="cdcf-g2" />
            <Num label="WACC (%)" value={dcf.wacc} step="0.1" onChange={(v) => setDcf({...dcf, wacc: v})} testId="cdcf-wacc" />
            <Num label="Ewiges Wachstum (%)" value={dcf.terminalGrowth} step="0.1" onChange={(v) => setDcf({...dcf, terminalGrowth: v})} testId="cdcf-tg" />
            <Num label="Nettoverschuldung (CHF)" value={dcf.netDebt} onChange={(v) => setDcf({...dcf, netDebt: v})} hint="Default = Σ Hypothek" testId="cdcf-debt" />
            <Num label="Sicherheitsmarge (%)" value={dcf.marginOfSafety} step="1" onChange={(v) => setDcf({...dcf, marginOfSafety: v})} testId="cdcf-mos" />
          </div>

          <h3 className="text-xs uppercase tracking-wider text-[#C4A55A] mt-6 mb-2">3b. NAV (Substanzwert) – aus {propCount} Immobilien</h3>
          {propCount > 0 ? (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 mb-3 text-xs text-emerald-200 flex gap-2">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                Auto-geladen: Σ Marktwert <strong>CHF {fmtInt(props.totalMarketValue)}</strong>,
                Σ Hypothek <strong>CHF {fmtInt(props.totalMortgage)}</strong>,
                NAV-Equity (vor sonstigem) = <strong>CHF {fmtInt(props.navEquity)}</strong>
              </div>
            </div>
          ) : (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200 flex items-center gap-2 mb-3">
              <AlertCircle className="h-4 w-4" />
              Keine Immobilien erfasst – NAV/Cap-Rate zeigen 0. <Link to="/admin/properties" className="underline ml-1">Immobilien anlegen</Link>.
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Num label="Sonstige Vermögenswerte (CHF)" value={capRate.otherAssets} onChange={(v) => setCapRate({...capRate, otherAssets: v})}
              hint="Beteiligungen, Cash, etc." testId="cnav-other-assets" />
            <Num label="Sonstige Verbindlichkeiten (CHF)" value={capRate.otherLiabilities} onChange={(v) => setCapRate({...capRate, otherLiabilities: v})}
              hint="Rückstellungen, etc." testId="cnav-other-liabs" />
          </div>

          <h3 className="text-xs uppercase tracking-wider text-[#C4A55A] mt-6 mb-2">3c. Cap Rate (Income-Approach)</h3>
          {propCount > 0 && (
            <div className="text-xs text-gray-400 mb-2">
              Σ NOI <strong className="text-white">CHF {fmtInt(props.totalNOI)}</strong> · Portfolio-Cap-Rate aktuell <strong className="text-white">{props.portfolioCapRate}%</strong>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Num label="Ziel-Cap-Rate (%)" value={capRate.targetCapRate} step="0.1" onChange={(v) => setCapRate({...capRate, targetCapRate: v})}
              hint="Marktüblich 4-6 % CH Wohnen" testId="cnav-target-cap" />
          </div>
        </section>

        {/* SCHRITT 4 – Gewichtung */}
        <section className="bg-[#0f0f0f] border border-white/10 rounded-2xl p-6" data-testid="combined-weights-section">
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-white/10 text-white font-bold rounded-full w-7 h-7 flex items-center justify-center text-sm">4</span>
            <Coins className="h-5 w-5 text-[#C4A55A]" />
            <h2 className="text-lg font-semibold">Gewichtung der Methoden</h2>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <Num label="DCF (%)" value={weights.dcf} step="5" onChange={(v) => setWeights({...weights, dcf: v})} testId="weight-dcf" />
            <Num label="NAV (%)" value={weights.nav} step="5" onChange={(v) => setWeights({...weights, nav: v})} testId="weight-nav" />
            <Num label="Cap Rate (%)" value={weights.cap} step="5" onChange={(v) => setWeights({...weights, cap: v})} testId="weight-cap" />
          </div>
          <div className={`text-xs ${Math.abs(totalWeight - 100) < 0.01 ? 'text-emerald-400' : 'text-amber-300'}`}>
            Σ Gewichte: {totalWeight}% {Math.abs(totalWeight - 100) < 0.01 ? '✓' : '(idealerweise 100%)'}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            💡 Für eine Immobilien-AG mit passiven Mieteinkünften ist eine höhere Gewichtung auf NAV + Cap Rate (z.B. 30/50/20) typisch.
          </p>
        </section>

        {/* SCHRITT 5 – Notiz + Berechnen */}
        <label className="block">
          <span className="text-xs text-gray-400">Notiz (optional)</span>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
            className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white"
            data-testid="combined-note" placeholder="z.B. Q1/2026 Konsensbewertung" />
        </label>

        <button
          onClick={runCombined}
          disabled={busy}
          data-testid="combined-calculate"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black font-bold text-lg disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Calculator className="h-5 w-5" />}
          Kombinierte Bewertung berechnen
        </button>

        {result && <CombinedResult result={result} properties={props?.items || []} />}
      </main>
    </div>
  );
};

const Num = ({ label, value, onChange, step = '1', hint, testId }) => (
  <label className="block">
    <span className="text-xs text-gray-400">{label}</span>
    <input type="number" step={step} value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white tabular-nums focus:border-[#C4A55A] focus:outline-none"
      data-testid={testId} />
    {hint && <span className="text-[10px] text-gray-500 mt-0.5 inline-block">{hint}</span>}
  </label>
);

const DataCard = ({ label, accent, children, empty, emptyHint, action }) => (
  <div className={`rounded-xl p-4 ${accent ? 'bg-black/40 border border-[#C4A55A]/30' : 'bg-black/40 border border-white/10'}`}>
    <div className="text-xs uppercase tracking-wider text-[#C4A55A] mb-2">{label}</div>
    {empty ? (
      <div className="text-xs text-gray-500">{emptyHint}</div>
    ) : (
      <div className="space-y-1">{children}</div>
    )}
    {action && (
      <button
        onClick={action.onClick}
        data-testid={action.testId}
        className="mt-3 w-full text-xs px-2 py-1.5 rounded-md bg-[#C4A55A]/15 border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/25"
      >
        {action.label}
      </button>
    )}
  </div>
);

const Row = ({ label, value, sub }) => (
  <div className="flex items-baseline justify-between text-sm">
    <span className="text-gray-400">{label}</span>
    <span className="text-white tabular-nums">
      {value}
      {sub && <span className="text-[10px] text-gray-500 ml-1">{sub}</span>}
    </span>
  </div>
);

const CombinedResult = ({ result, properties }) => (
  <section
    id="combined-result"
    className="bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-black border-2 border-[#C4A55A]/40 rounded-2xl p-6 space-y-6"
    data-testid="combined-result"
  >
    <div className="flex items-center justify-between">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <CheckCircle2 className="h-6 w-6 text-emerald-400" /> Bewertungs-Konsens
      </h2>
      <div className="flex items-center gap-3">
        {result.id && (
          <a
            href={`${apiBase()}/api/admin/valuation/combined/${result.id}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="combined-pdf-download"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#C4A55A]/15 border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/25 text-sm"
          >
            <Download className="h-4 w-4" /> PDF herunterladen
          </a>
        )}
        {result.note && <span className="text-xs text-gray-500 italic">„{result.note}"</span>}
      </div>
    </div>

    {/* Konvergenz Top-Tile */}
    <div className="bg-gradient-to-r from-[#C4A55A]/20 to-transparent border border-[#C4A55A] rounded-xl p-6"
         data-testid="convergence-tile">
      <div className="text-xs uppercase tracking-wider text-[#C4A55A] mb-2">Gewichteter Konsens</div>
      <div className="text-5xl font-bold text-[#C4A55A] tabular-nums mb-2">
        CHF {fmt(result.convergence.weightedValue)}
        <span className="text-sm text-gray-400 font-normal ml-2">/ Aktie</span>
      </div>
      <div className="text-xs text-gray-400">
        Spread zwischen Methoden: {result.convergence.spreadPercent} %
        {result.convergence.spreadPercent > 100 && ' – hoch, Methode prüfen'}
      </div>
    </div>

    {/* Drei Methoden mit Formel */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <MethodCard
        label="DCF (Ertragswert)"
        value={result.dcf.valuePerShare}
        formula="EV − Schuld) / Aktien"
        details={[
          ['Enterprise Value', `CHF ${fmtInt(result.dcf.enterpriseValue)}`],
          ['Equity Value', `CHF ${fmtInt(result.dcf.equityValue)}`],
          ['Aktien', fmtInt(result.dcf.inputs?.shares)],
        ]}
        weight={result.convergence.weights.dcf}
        testId="method-dcf"
      />
      <MethodCard
        label="NAV (Substanzwert)"
        value={result.nav.valuePerShare}
        formula="(ΣMV − ΣHyp + Sonst.) / Aktien"
        details={[
          ['Σ Marktwert', `CHF ${fmtInt(result.nav.totalMarketValue)}`],
          ['− Σ Hypothek', `CHF ${fmtInt(result.nav.totalMortgage)}`],
          ['+ Sonst. Aktiven', `CHF ${fmtInt(result.nav.otherAssets)}`],
          ['− Sonst. Passiven', `CHF ${fmtInt(result.nav.otherLiabilities)}`],
          ['= NAV-Equity', `CHF ${fmtInt(result.nav.navEquity)}`],
          ['Objekte', result.nav.propertyCount],
        ]}
        weight={result.convergence.weights.nav}
        testId="method-nav"
      />
      <MethodCard
        label="Cap Rate (Income)"
        value={result.capRate.valuePerShare}
        formula="(ΣNOI / Cap − Schuld) / Aktien"
        details={[
          ['Σ NOI', `CHF ${fmtInt(result.capRate.totalNOI)}`],
          ['Ziel-Cap-Rate', `${result.capRate.targetCapRate}%`],
          ['Aktueller Portfolio-Cap', `${result.capRate.currentPortfolioCapRate}%`],
          ['Portfolio-Value', `CHF ${fmtInt(result.capRate.portfolioValue)}`],
          ['− Schuld', `CHF ${fmtInt(result.capRate.netDebt)}`],
          ['= Equity', `CHF ${fmtInt(result.capRate.equityValue)}`],
        ]}
        weight={result.convergence.weights.cap}
        testId="method-cap"
      />
    </div>

    {/* Berücksichtigte Properties */}
    {properties.length > 0 && (
      <div data-testid="result-properties">
        <h3 className="text-sm uppercase tracking-wider text-[#C4A55A] mb-3 flex items-center gap-2">
          <Building2 className="h-4 w-4" /> Berücksichtigte Immobilien ({properties.length})
        </h3>
        <div className="overflow-x-auto bg-black/40 rounded-xl p-2">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 text-left">
                <th className="p-2">Adresse</th>
                <th className="p-2 text-right">Marktwert</th>
                <th className="p-2 text-right">Hypothek</th>
                <th className="p-2 text-right">Jahres-NOI</th>
                <th className="p-2 text-right">Cap-Rate</th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p) => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="p-2 text-gray-300">{p.address}</td>
                  <td className="p-2 text-right tabular-nums">CHF {fmtInt(p.marketValue)}</td>
                  <td className="p-2 text-right tabular-nums text-gray-400">CHF {fmtInt(p.mortgage)}</td>
                  <td className="p-2 text-right tabular-nums">CHF {fmtInt(p.annualNOI)}</td>
                  <td className="p-2 text-right tabular-nums">{p.capRate ? `${p.capRate}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {/* Scenarios */}
    {result.scenarios && (
      <div data-testid="scenarios-block">
        <h3 className="text-sm uppercase tracking-wider text-[#C4A55A] mb-3">Szenario-Analyse (DCF)</h3>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(result.scenarios).map(([k, v]) => (
            <div key={k} className={`rounded-xl p-4 border ${
              k === 'best' ? 'border-emerald-500/30 bg-emerald-500/5' :
              k === 'worst' ? 'border-red-500/30 bg-red-500/5' :
              'border-white/10 bg-black/40'
            }`} data-testid={`scenario-${k}`}>
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">{k}</div>
              <div className={`text-xl font-bold tabular-nums ${
                k === 'best' ? 'text-emerald-300' : k === 'worst' ? 'text-red-300' : 'text-white'
              }`}>
                CHF {v.valuePerShare ? fmt(v.valuePerShare) : '—'}
              </div>
              {v.inputs && (
                <div className="text-[10px] text-gray-500 mt-1">
                  g₁ {v.inputs.growth1}% · WACC {v.inputs.wacc}% · TG {v.inputs.terminalGrowth}%
                </div>
              )}
              {v.error && <div className="text-xs text-red-400">{v.error}</div>}
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Sensitivity matrix */}
    {result.sensitivity && (
      <div data-testid="sensitivity-block">
        <h3 className="text-sm uppercase tracking-wider text-[#C4A55A] mb-3">Sensitivitäts-Matrix · Wert pro Aktie</h3>
        <div className="overflow-x-auto bg-black/40 rounded-xl p-4">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left text-gray-500 p-2">WACC ↓ / TG →</th>
                {result.sensitivity.tgAxis.map((t) => (
                  <th key={t} className="text-right text-gray-400 p-2">{t}%</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.sensitivity.matrix.map((row, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="p-2 text-gray-400">{result.sensitivity.waccAxis[i]}%</td>
                  {row.map((cell, j) => {
                    const isCenter = i === Math.floor(result.sensitivity.matrix.length / 2)
                      && j === Math.floor(row.length / 2);
                    return (
                      <td key={j} className={`p-2 text-right tabular-nums ${
                        isCenter ? 'bg-[#C4A55A]/20 text-[#C4A55A] font-bold' : 'text-gray-300'
                      }`}>
                        {cell !== null ? fmtInt(cell) : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-gray-500 mt-2">Hervorgehoben: Base-Case-Annahmen.</p>
      </div>
    )}

    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200 flex gap-2">
      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
      Indikative interne Bewertung – nicht zur Veröffentlichung an Investoren bestimmt.
    </div>
  </section>
);

const MethodCard = ({ label, value, sub, formula, details, weight, testId }) => (
  <div className="bg-black/40 border border-white/10 rounded-xl p-4" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">{label}</div>
    <div className="text-2xl font-bold tabular-nums text-white">CHF {fmt(value)}</div>
    {formula && <div className="text-[10px] text-gray-500 mt-1 font-mono">{formula}</div>}
    {details && (
      <div className="mt-3 space-y-0.5 border-t border-white/5 pt-2">
        {details.map(([k, v]) => (
          <div key={k} className="flex justify-between text-[11px] text-gray-400">
            <span>{k}</span>
            <span className="text-gray-200 tabular-nums">{v}</span>
          </div>
        ))}
      </div>
    )}
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    {weight !== undefined && (
      <div className="mt-2 text-[10px] text-[#C4A55A]">Gewicht: {weight}%</div>
    )}
  </div>
);
